import dayjs from "dayjs";
import utc from "dayjs/plugin/utc.js";
import timezone from "dayjs/plugin/timezone.js";
import { RRule } from "rrule";

dayjs.extend(utc);
dayjs.extend(timezone);

export type daysOfWeek =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

export const RRuleWeekday = {
  monday: "MO",
  tuesday: "TU",
  wednesday: "WE",
  thursday: "TH",
  friday: "FR",
  saturday: "SA",
  sunday: "SU",
} as const;

type DeliveryAreaType = {
  id: number;
  startAt: Date;
  name: string;
  weeklyCadence: number | null;
  orderCutOffTime: Date | null;
  orderCutOffDaysBefore: number | null;
  includeWeekend: boolean;
  systemDefinedType: "sellarDistribution" | null;
  systemDefinedLocation: string | null;
  DeliveryAreaDays: {
    day: daysOfWeek;
  }[];
  DeliveryAreaPostcodes: {
    postcodeComponent: string;
  }[];
};

const sortDeliveryAreaByDate = (
  deliveryAreas: DeliveryAreaType[],
  startDayRef?: Date,
) => {
  const startDateTime = startDayRef ?? new Date();
  return deliveryAreas.sort((deliveryAreaA, deliveryAreaB) => {
    const deliveryAreaADeliveryDates = getNextDeliveryDate({
      ...deliveryAreaA,
      startDateReference: startDateTime,
      deliveryAreaDays: deliveryAreaA.DeliveryAreaDays,
    });

    const deliveryAreaBDeliveryDates = getNextDeliveryDate({
      ...deliveryAreaB,
      startDateReference: startDateTime,
      deliveryAreaDays: deliveryAreaB.DeliveryAreaDays,
    });

    const deliveryAreaADeliveryDateValue = deliveryAreaADeliveryDates.isSuccess
      ? deliveryAreaADeliveryDates.data.deliveryDate.valueOf()
      : 0;
    const deliveryAreaBDeliveryDateValue = deliveryAreaBDeliveryDates.isSuccess
      ? deliveryAreaBDeliveryDates.data.deliveryDate.valueOf()
      : 0;

    return deliveryAreaADeliveryDateValue - deliveryAreaBDeliveryDateValue;
  });
};

export const getSpecificDeliveryArea = ({
  deliveryAreas,
  startDayRef,
}: {
  deliveryAreas: DeliveryAreaType[];
  startDayRef?: Date;
}) => {
  return deliveryAreas.length > 1
    ? sortDeliveryAreaByDate(deliveryAreas, startDayRef)?.[0]
    : deliveryAreas?.[0];
};

export const subtractBusinessDays = (date: dayjs.Dayjs, days: number) => {
  let result = date;
  let remaining = days;
  while (remaining > 0) {
    result = result.subtract(1, "day");
    const day = result.day(); // 0 = Sunday, 6 = Saturday
    if (day !== 0 && day !== 6) {
      remaining--;
    }
  }
  return result;
};

export const addWorkingDays = (
  date: dayjs.Dayjs,
  days: number,
  nonWorkingDays: Date[] = [],
) => {
  const isNonWorkingDay = (d: dayjs.Dayjs) => {
    const dayOfWeek = d.day(); // 0 = Sunday, 6 = Saturday
    if (dayOfWeek === 0 || dayOfWeek === 6) return true;
    return nonWorkingDays.some((nwd) =>
      dayjs(nwd).tz("Europe/London").isSame(d, "day"),
    );
  };

  // If the start date itself isn't a working day (e.g. it lands on a
  // weekend or a bank holiday Monday), nothing can actually happen that
  // day, so roll forward to the next working day before counting.
  let result = date;
  while (isNonWorkingDay(result)) {
    result = result.add(1, "day");
  }

  let remaining = days;
  while (remaining > 0) {
    result = result.add(1, "day");
    if (!isNonWorkingDay(result)) {
      remaining--;
    }
  }
  return result;
};

const getDispatchPlannedAt = ({
  now,
  cutoffHour,
  cutoffMinute,
  includeWeekend,
}: {
  now: dayjs.Dayjs;
  cutoffHour: number;
  cutoffMinute: number;
  includeWeekend: boolean;
}) => {
  const today = now.startOf("day");
  const isWeekend = (d: dayjs.Dayjs) => d.day() === 0 || d.day() === 6;

  const cutoff = today.set("hour", cutoffHour).set("minute", cutoffMinute);

  let dispatchDate: dayjs.Dayjs;

  // --- Before cutoff → dispatch today ---
  if (now.isBefore(cutoff)) {
    dispatchDate = today;

    if (!includeWeekend && isWeekend(dispatchDate)) {
      dispatchDate = today.day(today.day() === 6 ? 8 : 1); // Monday
    }
  }

  // --- After cutoff → dispatch tomorrow ---
  else {
    dispatchDate = today.add(1, "day");

    // If today is Friday → next Monday
    if (!includeWeekend && today.day() === 5) {
      dispatchDate = today.day(8);
    }

    // If tomorrow is weekend → next Monday
    if (!includeWeekend && isWeekend(dispatchDate)) {
      dispatchDate = dispatchDate.day(1);
    }
  }

  return dispatchDate.toDate();
};

export type GetNextDeliveryDateParams = {
  startDateReference: Date;
  startAt: Date; // only considers the date
  weeklyCadence: number | null;
  deliveryAreaDays: { day: daysOfWeek }[];
  orderCutOffTime: Date | null; // only considers hours and minutes
  orderCutOffDaysBefore: number | null;
  includeWeekend: boolean;
  nextCollectionDate?: Date | null;
};

export const getNextDeliveryDate = ({
  startDateReference,
  startAt,
  weeklyCadence,
  deliveryAreaDays,
  orderCutOffTime,
  orderCutOffDaysBefore,
  includeWeekend,
  nextCollectionDate,
}: GetNextDeliveryDateParams):
  | { isSuccess: true; data: { deliveryDate: Date; dispatchDate?: Date } }
  | { isSuccess: false; message: string } => {
  if (!deliveryAreaDays?.length) {
    return {
      isSuccess: false,
      message: "Next delivery date not found, no delivery area dates provided.",
    };
  }

  if (typeof weeklyCadence === "number" && weeklyCadence < 0) {
    return {
      isSuccess: false,
      message: "Weekly cadence cannot be less than 0.",
    };
  }

  if (typeof orderCutOffDaysBefore === "number" && orderCutOffDaysBefore < 0) {
    return {
      isSuccess: false,
      message: "orderCutOffDaysBefore cannot be less than 0.",
    };
  }

  if (orderCutOffTime && orderCutOffDaysBefore === null) {
    return {
      isSuccess: false,
      message: "orderCutOffDaysBefore required if orderCutOffTime provided.",
    };
  }

  const deliveryAreaDaysAsRRuleDays = deliveryAreaDays.map(
    (deliveryAreaDay) => RRuleWeekday[deliveryAreaDay.day],
  );

  const currentDateTimeUtc = dayjs(startDateReference).utc();

  const rule = new RRule({
    // 2 means weekly enum, using as type import not compatible with esm
    freq: 2,
    interval: weeklyCadence ?? 1,
    byweekday: deliveryAreaDaysAsRRuleDays,
    dtstart: dayjs(startAt).utc().startOf("day").toDate(), // delivery date is always the start of day 00:00:00
  });

  const potentialDeliveryDates = getPotentialNextDeliveryDates({
    limit: 10,
    rule,
    startDate: currentDateTimeUtc.toDate(),
  });

  const providedCutoffTimeUtc = orderCutOffTime
    ? dayjs(orderCutOffTime).utc()
    : undefined;

  const cutOffHour = providedCutoffTimeUtc?.get("hour") ?? 23;
  const cutOffMinute = providedCutoffTimeUtc?.get("minute") ?? 59;

  const potentialDeliveryDatesWithCutOffs = potentialDeliveryDates.map(
    (potentialDeliveryDate) => {
      const nextDeliveryDateLocal = dayjs(potentialDeliveryDate)
        .utc()
        .tz("Europe/London");

      const cutoffBase = includeWeekend
        ? nextDeliveryDateLocal.subtract(orderCutOffDaysBefore ?? 0, "day")
        : subtractBusinessDays(
            nextDeliveryDateLocal,
            orderCutOffDaysBefore ?? 0,
          );

      const potentialDeliveryDateCutOff = cutoffBase
        .set("hour", cutOffHour)
        .set("minute", cutOffMinute)
        .toDate();

      return {
        potentialDeliveryDate,
        potentialDeliveryDateCutOff,
      };
    },
  );

  const sortedPotentialDeliveryDatesWithCutOffs =
    potentialDeliveryDatesWithCutOffs.toSorted(
      (a, b) =>
        a.potentialDeliveryDateCutOff.getTime() -
        b.potentialDeliveryDateCutOff.getTime(),
    );

  const result = sortedPotentialDeliveryDatesWithCutOffs.find(
    (item) =>
      dayjs(currentDateTimeUtc).isBefore(item.potentialDeliveryDateCutOff) ||
      dayjs(currentDateTimeUtc).isSame(item.potentialDeliveryDateCutOff),
  );

  const dispatchDate = getDispatchPlannedAt({
    now: currentDateTimeUtc,
    cutoffHour: cutOffHour,
    cutoffMinute: cutOffMinute,
    includeWeekend,
  });

  const nextDeliveryDate =
    result?.potentialDeliveryDate ?? currentDateTimeUtc.startOf("day").toDate();

  return {
    isSuccess: true,
    data: {
      deliveryDate: ensureDeliveryDateIsAfterCollectionDate({
        rule,
        collectionDate: nextCollectionDate ?? null,
        deliveryDate: nextDeliveryDate,
      }),
      ...(typeof orderCutOffDaysBefore === "number"
        ? {
            dispatchDate,
          }
        : {}),
    },
  };
};

const getPotentialNextDeliveryDates = ({
  limit,
  rule,
  startDate,
}: {
  limit: number;
  rule: RRule;
  startDate: Date;
}) => {
  let nextDate = startDate;
  const deliveryDates = new Array(limit).fill(true).map(() => {
    nextDate = rule.after(nextDate, false) ?? nextDate;
    return nextDate;
  });

  return deliveryDates;
};

/**
 * Whenever a collection date is provided we need to make sure
 * the delivery date is after that collection date. (e.g Sellar Distribution)
 */
const ensureDeliveryDateIsAfterCollectionDate = ({
  rule,
  collectionDate,
  deliveryDate,
}: {
  rule: RRule;
  deliveryDate: Date;
  collectionDate: Date | null;
}) => {
  if (!collectionDate) {
    return deliveryDate;
  }

  const deliveryDateUtc = dayjs(deliveryDate).utc();
  const collectionDateUtc = dayjs(collectionDate).utc();

  if (deliveryDateUtc.isAfter(collectionDateUtc)) {
    return deliveryDate;
  }

  const nextDeliveryDate = rule.after(collectionDateUtc.toDate(), false);

  return nextDeliveryDate ?? deliveryDate;
};

export const supplierPalletShipmentLeadTimeDays = 1; // inclusive of day on which pallet orders can begin being delivered to customers
export const supplierPalletShipmentLeadTimeDaysBankHolidayWeek = 2;

// ensure these cutoffs match the finalise-supplier-pallet-shipments cron task
export const supplierPalletShipmentOrderCutoffDay = 1; // 0=sunday, monday=1 etc.
export const supplierPalletShipmentOrderCutoffHours = 10; // am
export const supplierPalletShipmentOrderCutoffMinutes = 30;

// How many whole weeks the week containing `cutoffDateTime` sits past the
// last on-cycle week, e.g. 0 = on-cycle, 1 = one week past the last on-cycle week.
const getWeeksPastLastOnCycleWeek = (
  cutoffDateTime: Date,
  {
    weekInterval,
    anchorStartAt,
  }: { weekInterval: number; anchorStartAt: Date },
): number => {
  const anchorMonday = dayjs(anchorStartAt)
    .tz("Europe/London")
    .startOf("week")
    .add(1, "day")
    .startOf("day");

  const cutoffMonday = dayjs(cutoffDateTime)
    .tz("Europe/London")
    .startOf("week")
    .add(1, "day")
    .startOf("day");

  const weeksSinceAnchor = Math.round(
    cutoffMonday.diff(anchorMonday, "day") / 7,
  );

  return ((weeksSinceAnchor % weekInterval) + weekInterval) % weekInterval;
};

// Determines whether a given cutoff/window date falls on a supplier's
// on-cycle week for their pallet inbound cadence.
export const isOrderByDateOnCycle = (
  cutoffDateTime: Date,
  {
    weekInterval,
    anchorStartAt,
  }: { weekInterval: number; anchorStartAt: Date },
): boolean => {
  if (weekInterval <= 1) {
    return true;
  }

  return (
    getWeeksPastLastOnCycleWeek(cutoffDateTime, {
      weekInterval,
      anchorStartAt,
    }) === 0
  );
};

export const getOrderByDateForNextPalletShipment = (
  startRefDateTime: Date,
  optionalArguments: {
    cutoffDay?: number;
    cutoffHours?: number;
    cutoffMinutes?: number;
    // Number of weeks between ship cycles for this supplier (1 = every week, 2 = every other week etc)
    weekInterval?: number;
    // Anchors which week is "week one" of the cycle; required when weekInterval > 1
    anchorStartAt?: Date;
  } = {},
) => {
  const cutoffDay =
    optionalArguments.cutoffDay ?? supplierPalletShipmentOrderCutoffDay;
  const cutoffHours =
    optionalArguments.cutoffHours ?? supplierPalletShipmentOrderCutoffHours;
  const cutoffMinutes =
    optionalArguments.cutoffMinutes ?? supplierPalletShipmentOrderCutoffMinutes;
  const weekInterval = optionalArguments.weekInterval ?? 1;
  const anchorStartAt = optionalArguments.anchorStartAt;

  const startDayRef = dayjs(startRefDateTime);

  let cutoff = startDayRef
    .tz("Europe/London")
    .day(cutoffDay)
    .hour(cutoffHours)
    .minute(cutoffMinutes)
    .second(0)
    .millisecond(0);

  // If start day reference is at or after cutoff, move to next week's cutoff
  if (!startDayRef.isBefore(cutoff)) {
    cutoff = cutoff
      .add(1, "week")
      .tz("Europe/London") // timezone is set after the date is moved to ensure correct timezone
      .day(cutoffDay)
      .hour(cutoffHours)
      .minute(cutoffMinutes);
  }

  // Weekly suppliers (the default) accept every cutoff as valid, matching prior behaviour.
  if (weekInterval <= 1 || !anchorStartAt) {
    return cutoff.toDate();
  }

  // Fortnightly (or longer) suppliers only ship on cutoffs that fall on-cycle relative to their anchor week.
  const weeksPastLastOnCycleWeek = getWeeksPastLastOnCycleWeek(
    cutoff.toDate(),
    { weekInterval, anchorStartAt },
  );

  // If already on-cycle, no adjustment needed; otherwise jump forward to the
  // next on-cycle week instead of stepping through each week in a loop.
  const weeksToAdd =
    weeksPastLastOnCycleWeek === 0
      ? 0
      : weekInterval - weeksPastLastOnCycleWeek;

  if (weeksToAdd > 0) {
    cutoff = cutoff
      .add(weeksToAdd, "week")
      .tz("Europe/London") // timezone is set after the date is moved to ensure correct timezone
      .day(cutoffDay)
      .hour(cutoffHours)
      .minute(cutoffMinutes)
      .second(0)
      .millisecond(0);
  }

  return cutoff.toDate();
};

export const getSupplierPalletShipmentLeadTimeDays = ({
  orderCutoffDateTime,
  nonWorkingDayMondays,
  leadDays = supplierPalletShipmentLeadTimeDays,
  bankHolidayWeekLeadDays = supplierPalletShipmentLeadTimeDaysBankHolidayWeek,
}: {
  orderCutoffDateTime: Date;
  nonWorkingDayMondays: Date[];
  leadDays?: number;
  bankHolidayWeekLeadDays?: number;
}) => {
  const cutoffWeekMonday = dayjs(orderCutoffDateTime)
    .tz("Europe/London")
    .startOf("week")
    .add(1, "day")
    .startOf("day");

  const hasBankHolidayMonday = nonWorkingDayMondays.some(
    (nonWorkingDayMonday) =>
      dayjs(nonWorkingDayMonday)
        .tz("Europe/London")
        .startOf("day")
        .isSame(cutoffWeekMonday, "day"),
  );

  return hasBankHolidayMonday ? bankHolidayWeekLeadDays : leadDays;
};

export const getNextSupplierPalletShipmentArrivalDate = (
  startRefDateTime: Date,
  optionalArguments: {
    cutoffDay?: number;
    cutoffHours?: number;
    cutoffMinutes?: number;
    leadDays?: number;
    bankHolidayWeekLeadDays?: number;
    nonWorkingDayMondays?: Date[];
    weekInterval?: number;
    anchorStartAt?: Date;
  } = {},
): Date => {
  const cutoffDay =
    optionalArguments.cutoffDay ?? supplierPalletShipmentOrderCutoffDay;
  const cutoffHours =
    optionalArguments.cutoffHours ?? supplierPalletShipmentOrderCutoffHours;
  const cutoffMinutes =
    optionalArguments.cutoffMinutes ?? supplierPalletShipmentOrderCutoffMinutes;
  const leadDays =
    optionalArguments.leadDays ?? supplierPalletShipmentLeadTimeDays;
  const bankHolidayWeekLeadDays =
    optionalArguments.bankHolidayWeekLeadDays ??
    supplierPalletShipmentLeadTimeDaysBankHolidayWeek;
  const nonWorkingDayMondays = optionalArguments.nonWorkingDayMondays ?? [];
  const weekInterval = optionalArguments.weekInterval ?? 1;
  const anchorStartAt = optionalArguments.anchorStartAt;

  const orderCutoffDateTime = getOrderByDateForNextPalletShipment(
    startRefDateTime,
    {
      cutoffDay,
      cutoffHours,
      cutoffMinutes,
      weekInterval,
      anchorStartAt,
    },
  );

  const leadTimeDays = getSupplierPalletShipmentLeadTimeDays({
    orderCutoffDateTime,
    nonWorkingDayMondays,
    leadDays,
    bankHolidayWeekLeadDays,
  });

  return dayjs(orderCutoffDateTime)
    .tz("Europe/London")
    .add(leadTimeDays, "days")
    .set("hour", 12) // Noon
    .set("minute", 0)
    .set("second", 0)
    .set("millisecond", 0)
    .toDate();
};

export const getNextStartDate = ({
  latestAvailablePreOrderFromDate,
  outOfOfficeReturnDate,
  courierDeliveryScheduleStartDate,
  currentDate,
}: {
  outOfOfficeReturnDate: Date | null;
  latestAvailablePreOrderFromDate: Date | null;
  courierDeliveryScheduleStartDate: Date | null;
  currentDate: Date;
}) => {
  const validDates = [
    latestAvailablePreOrderFromDate,
    outOfOfficeReturnDate,
    courierDeliveryScheduleStartDate,
  ]
    .filter((date) => date !== null) // Exclude null values
    .map((date) => dayjs(date)); // Convert to dayjs objects

  const latestDate =
    validDates.length > 0
      ? validDates
          .reduce((latest, current) =>
            current.isAfter(latest) ? current : latest,
          )
          .toDate()
      : currentDate;

  return latestDate;
};
